/* eslint-disable max-lines -- structural: this file owns both API and
   static-option dropdown renderers (read-only, editable, and form) plus
   the bulk-edit clear UX. Splitting it adds indirection without value. */
/**
 * Dropdown Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for dropdown columns
 * (API-backed and static options).
 */

import { memo, useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import type { ComboBoxApi, ComboBoxOption } from 'components/inputs/types';
import { Select, ComboBox, Autocomplete, Chip } from 'components';
import { Tooltip } from 'components/tooltip';
import { CaretDown } from '@phosphor-icons/react';
import { t } from '@lingui/macro';
import { toast } from 'react-toastify';
import { TableTooltip } from '../../TableTooltip';
import { useEditableContextOptional } from '../../OptimizedTableProvider';
import { EditableDropdownCell, ReadOnlyDropdownCell } from './EditableDropdownCell';
import { SharedEditableCell } from './SharedEditableCell';
import { clearEditOnSuccess } from './inlineEditUtils';
import { CellContainer, InvalidTag, TagsContainer } from '../../tableStyles';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import type { CustomFieldValue } from '../../customFields';
import { getCellValue } from '../cellValue';
import { getLookupApiFn } from '../dropdown';
import { createFilterOptions } from '@mui/material';

type BulkBrandSupplierErrors = {
  brand?: string;
  supplierContactPerson?: string;
  severity?: { brand?: 'error' | 'warning'; supplierContactPerson?: 'error' | 'warning' };
};

const defaultComboBoxFilter = createFilterOptions<ComboBoxOption>();

// ============================================================================
// Validation Helper
// ============================================================================

/**
 * Check if a value is valid (exists in the allowed options)
 * Returns true if value is empty or found in options, false if value exists but not in options.
 * Uses case-insensitive label comparison so API values like "published" match option "Published".
 */
const isValueValid = (
  cellValue: string,
  options?: Array<{ id: string | number; label: string }>
): boolean => {
  // Empty values are always valid
  if (!cellValue) return true;
  // If no options provided, we can't validate - assume valid
  if (!options || options.length === 0) return true;
  // Check if value exists in options (match by label or id; label comparison is case-insensitive)
  const cellLower = cellValue.toLowerCase();
  return options.some(
    opt =>
      (typeof opt.label === 'string' && opt.label.toLowerCase() === cellLower) ||
      String(opt.id) === cellValue
  );
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Helper to get display value from cell context for dropdown
 * Uses getCellValue (respects accessorFn) and handles array/object formats
 */
const getCellDisplayValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>
): string => {
  const value = getCellValue(context, context.column.id);

  if (value === undefined || value === null) {
    return '';
  }

  // Handle array values (multi-select)
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Handle object with 'name' property
  if (typeof value === 'object' && 'name' in value) {
    return String((value as { name: string }).name);
  }

  return String(value);
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Memoized chip cell for dropdown with colors
 */
const DropdownChipCell = memo(function DropdownChipCell({
  label,
  chipColor,
}: {
  label: string;
  chipColor: string;
}) {
  return (
    <Chip
      sx={{ padding: '4px 16px' }}
      isBordered={false}
      isBackgroundFilled
      isTooltip={false}
      label={label}
      color={chipColor}
      background={chipColor}
    />
  );
});

/**
 * Dropdown read-only renderer - displays value with optional avatar
 * Uses col.id as the data field
 * If optionColors is provided, renders a colored chip instead
 */
export const dropdownReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  // Check if optionColors is provided for colored chip display
  if (col.optionColors) {
    return {
      cell: (context: CellContext<T, unknown>) => {
        const value = getCellValue(context, col.id);
        const id =
          typeof value === 'object' && value !== null
            ? (value as { id: string | number }).id
            : value;
        const label = getCellDisplayValue(context);
        const chipColor = col.optionColors?.[id as string | number] || 'grey';
        return <DropdownChipCell label={label} chipColor={chipColor} />;
      },
      tooltipRender: (cell: CellContext<T, unknown>) => getCellDisplayValue(cell),
    };
  }

  // Get showAvatar from column config (default true)
  const showAvatar = col.showAvatar ?? true;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const cellValue = getCellDisplayValue(context);
      return <ReadOnlyDropdownCell value={cellValue || ''} showAvatar={showAvatar} />;
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      // Tooltip also has CellContext, so use getCellDisplayValue
      return getCellDisplayValue(cell);
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Dropdown editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 * Supports both API-backed and static options
 */
export const dropdownEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const resolvedApi = col.api || (getLookupApiFn ? getLookupApiFn(col.id) : null);
  const hasStaticOptions = col.options && col.options.length > 0;
  const hasNoDataSource = !resolvedApi && !hasStaticOptions;
  const showAvatar = col.showAvatar ?? Boolean(resolvedApi);
  const editableProp = col.editable;

  // If no data source available, render as read-only text
  // This gracefully handles custom fields where allowed values aren't loaded
  if (hasNoDataSource) {
    return {
      cell: (context: CellContext<T, unknown>) => {
        const cellValue = getCellDisplayValue(context);
        return <CellContainer>{cellValue || ''}</CellContainer>;
      },
      meta: {
        editable: false,
      },
    };
  }

  // If we have an API, use the API-backed EditableDropdownCell
  if (resolvedApi) {
    // Type assertion needed because BaseColumn api.url can be null, but we've already checked it exists
    const apiConfig = resolvedApi as NonNullable<ComboBoxApi>;

    return {
      cell: (context: CellContext<T, unknown>) => {
        // Use getCellDisplayValue to respect accessorFn (important for custom fields)
        const cellValue = getCellDisplayValue(context);

        // Evaluate editability for this specific row
        const isRowEditable =
          typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

        // If not editable for this row, show tooltip when disabled with a reason
        if (!isRowEditable) {
          const disabledTooltip = col.editDisabledPlaceholder
            ? typeof col.editDisabledPlaceholder === 'function'
              ? col.editDisabledPlaceholder()
              : col.editDisabledPlaceholder
            : undefined;
          const displayValue = cellValue || '';
          const showPlaceholder = !cellValue && Boolean(disabledTooltip);
          const cell = (
            <CellContainer
              style={{
                color: showPlaceholder ? '#999' : 'inherit',
                fontStyle: showPlaceholder ? 'italic' : 'normal',
              }}
            >
              {displayValue || (showPlaceholder ? disabledTooltip : '')}
            </CellContainer>
          );
          return disabledTooltip ? (
            <TableTooltip title={disabledTooltip}>{cell}</TableTooltip>
          ) : (
            cell
          );
        }

        return (
          <EditableDropdownCell<T>
            context={context}
            api={apiConfig}
            value={cellValue}
            showAvatar={showAvatar}
            onChange={(e, selectedValue) => {
              if (!col.onUpdate) return;
              let result: void | Promise<void>;
              if (
                selectedValue &&
                typeof selectedValue !== 'string' &&
                !Array.isArray(selectedValue) &&
                selectedValue.id
              ) {
                if (selectedValue.id === '__clear__') {
                  if ('required' in col) {
                    toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
                    return;
                  }
                  result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
                } else {
                  const updateValue = {
                    id: selectedValue.id,
                    name: selectedValue.label || '',
                  };
                  result = col.onUpdate(context.row.original, {
                    [col.id]: updateValue,
                  } as Partial<T>);
                }
              } else {
                if ('required' in col) {
                  toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
                  return;
                }
                result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
              }
              return result;
            }}
          />
        );
      },
      meta: {
        editable: true,
      },
    };
  }

  // Static options path - use Autocomplete with static options
  // Get color from optionColors map if provided
  const getChipColor = (id: string | number) => col.optionColors?.[id] || 'grey';

  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use getCellDisplayValue to respect accessorFn (important for custom fields)
      const cellValue = getCellDisplayValue(context);

      // Extract id directly from cell value for color lookup (more reliable than string matching)
      const rawValue = getCellValue(context, col.id);
      const valueId =
        typeof rawValue === 'object' && rawValue !== null
          ? (rawValue as { id: string | number }).id
          : rawValue;

      // Evaluate editability for this specific row
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      // Find current value in options
      const currentOption = col.options?.find(
        opt => opt.label === cellValue || String(opt.id) === cellValue
      );

      // Check if value is valid (exists in allowed options)
      const isValid = isValueValid(cellValue, col.options);
      const tooltipText = cellValue ? (isValid ? cellValue : `${cellValue} (invalid value)`) : '';

      // Read-only display - use chip if optionColors is provided
      // Use valueId for color lookup (falls back to currentOption?.id for compatibility)
      // Show InvalidTag if value doesn't exist in options
      const chipColor = getChipColor((valueId ?? currentOption?.id ?? '') as string | number);
      const readOnlyDisplay = col.optionColors ? (
        <Chip
          sx={{ padding: '4px 16px' }}
          isBordered={false}
          isBackgroundFilled
          isTooltip={false}
          label={cellValue}
          color={isValid ? chipColor : 'red'}
          background={isValid ? chipColor : 'red'}
        />
      ) : cellValue && !isValid ? (
        <TableTooltip title={tooltipText}>
          <CellContainer>
            <TagsContainer>
              <InvalidTag>{cellValue}</InvalidTag>
            </TagsContainer>
          </CellContainer>
        </TableTooltip>
      ) : (
        <CellContainer>{cellValue || ''}</CellContainer>
      );

      // If not editable for this row, show tooltip when disabled with a reason
      if (!isRowEditable) {
        const disabledTooltip = col.editDisabledPlaceholder
          ? typeof col.editDisabledPlaceholder === 'function'
            ? col.editDisabledPlaceholder()
            : col.editDisabledPlaceholder
          : undefined;
        // Check for empty value more explicitly (empty string, null, undefined, whitespace)
        const isEmpty = !cellValue || (typeof cellValue === 'string' && cellValue.trim() === '');
        if (isEmpty && disabledTooltip) {
          const placeholderCell = (
            <CellContainer style={{ color: '#999', fontStyle: 'italic' }}>
              {disabledTooltip}
            </CellContainer>
          );
          return <TableTooltip title={disabledTooltip}>{placeholderCell}</TableTooltip>;
        }
        return readOnlyDisplay;
      }

      // Filter options for hierarchical custom fields (NestedAllowedValue)
      let filteredOptions = col.options || [];

      // CROSS-FIELD DEPENDENCY: Filter based on parent custom field's selected value
      // If this field has a parentCustomFieldId, filter options to only show children
      // of the parent field's selected value
      if ((col as { parentCustomFieldId?: number }).parentCustomFieldId) {
        const parentCustomFieldId = (col as { parentCustomFieldId?: number }).parentCustomFieldId;

        // Get the parent field's value from the row
        // Find the parent field in the row's customFields array
        const parentFieldValue = (
          context.row.original as { customFields?: CustomFieldValue[] }
        ).customFields?.find(cf => cf.customFieldId === parentCustomFieldId);

        const parentSelectedId = parentFieldValue?.itemCustomFieldAllowedValueId;

        if (parentSelectedId) {
          // Parent has a value selected - filter to show only children of that value
          filteredOptions = filteredOptions.filter(
            (opt: ComboBoxOption) => opt.customProps?.parentId === parentSelectedId
          );

          // If no children found, keep empty (user must select parent first)
          // Don't fallback to all options like we do for within-field hierarchy
        }
        // If parent not selected, show no options (placeholder will show "Select parent first")
        else {
          filteredOptions = [];
        }
      }
      // WITHIN-FIELD HIERARCHY: Filter based on current field's selected value
      // Check if this is a hierarchical field by looking for parentId in customProps
      // Note: parentId can be null, undefined, or a number. We check if the property exists.
      const hasHierarchy = filteredOptions.some(
        (opt: ComboBoxOption) => opt.customProps?.parentId !== undefined
      );

      if (hasHierarchy) {
        // This is a hierarchical field - filter options based on current selection
        const currentValueId = currentOption?.id;

        // Helper to check if option is a parent-level option (no parent)
        const isParentOption = (opt: ComboBoxOption): boolean => {
          const parentId = opt.customProps?.parentId;
          return parentId === null || parentId === undefined || parentId === 0;
        };

        if (!currentValueId) {
          // No value selected - show only parent options
          filteredOptions = filteredOptions.filter(isParentOption);
        } else {
          // Value selected - show children of the selected value
          filteredOptions = filteredOptions.filter(
            (opt: ComboBoxOption) => opt.customProps?.parentId === currentValueId
          );

          // If no children found, show all parent-level options
          // (this allows user to change their selection)
          if (filteredOptions.length === 0) {
            filteredOptions = (col.options || []).filter(isParentOption);
          }
        }
        // Defensive fallback: if filtering resulted in no options, show all options
        // This handles edge cases where data might be malformed
        if (filteredOptions.length === 0) {
          filteredOptions = col.options || [];
        }
      }

      // Add "Clear" option at the top when there's a value (but not for required fields)
      const clearOption = { id: '__clear__', label: t`Clear` };
      const optionsWithClear =
        cellValue && !('required' in col) ? [clearOption, ...filteredOptions] : filteredOptions;

      return (
        <DropdownStaticOptionsCell
          context={context}
          col={col}
          cellValue={cellValue}
          currentOption={currentOption ?? null}
          optionsWithClear={optionsWithClear}
          readOnlyDisplay={readOnlyDisplay}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

/** Wrapper that uses editable context for static-options dropdown path */
const DropdownStaticOptionsCell = <T extends { id: number | string }>({
  context,
  col,
  cellValue,
  currentOption,
  optionsWithClear,
  readOnlyDisplay,
}: {
  context: CellContext<T, unknown>;
  col: CustomColumn<T>;
  cellValue: string;
  currentOption: ComboBoxOption | null;
  optionsWithClear: ComboBoxOption[];
  readOnlyDisplay: React.ReactNode;
}) => {
  const editableCtx = useEditableContextOptional();
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);
  const clearEdit = useCallback(
    () => setEditableState({ rowIndex: null, columnId: null }),
    [setEditableState]
  );

  return (
    <SharedEditableCell
      {...context}
      meta={{ editable: true }}
      tooltipTitle={cellValue}
      readModeEl={readOnlyDisplay}
    >
      <Autocomplete
        id={`${col.id}-edit`}
        options={optionsWithClear}
        variant="standard"
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        value={currentOption ?? null}
        onChange={(_, newValue) => {
          if (!col.onUpdate) {
            clearEdit();
            return;
          }
          let result: void | Promise<void>;
          if (newValue?.id === '__clear__') {
            if ('required' in col) {
              toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
              clearEdit();
              return;
            }
            result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
          } else if (newValue && typeof newValue === 'object' && 'id' in newValue) {
            const updateValue = {
              id: newValue.id,
              name: newValue.label || '',
            };
            result = col.onUpdate(context.row.original, {
              [col.id]: updateValue,
            } as Partial<T>);
          } else {
            if ('required' in col) {
              toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
              clearEdit();
              return;
            }
            result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
          }
          clearEditOnSuccess(result, clearEdit);
        }}
        onBlur={clearEdit}
        onKeyDown={e => {
          if (e.key === 'Enter') clearEdit();
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.id === '__clear__') return false;
          return String(option.id) === String(val.id);
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          if (option?.id === '__clear__') return '';
          return option?.label ?? '';
        }}
        autoFocus
        openOnFocus
        sx={{ minWidth: 150 }}
      />
    </SharedEditableCell>
  );
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const dropdownReadOnly: ColumnRenderer = dropdownReadOnlyRenderer;

/** Editable renderer with proper typing */
export const dropdownEditable: ColumnRenderer = dropdownEditableRenderer;
